import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { withWorkspaceFilter, getActiveWorkspaceId } from "@/utils/workspace-helpers";

export interface TripletexIntegration {
  id: string;
  company_name: string | null;
  organization_number: string | null;
  is_active: boolean;
  created_at: string;
  session_expires_at: string | null;
  company_id: string;
}

export function useTripletexIntegration() {
  const useHasActiveTripletexQuery = () => {
    return useQuery({
      queryKey: ["has-active-tripletex", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { data, error } = await withWorkspaceFilter(
          supabase.from("tripletex_integrations")
          .select("id")
          .eq("is_active", true)
          .limit(1)
        );

        if (error) {
          throw error;
        }

        return data && data.length > 0;
      },
    });
  };

  const useLastSyncTimeQuery = () => {
    return useQuery({
      queryKey: ["tripletex-last-sync", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await withWorkspaceFilter(
          supabase.from("tripletex_detection_runs")
          .select("run_at")
          .order("run_at", { ascending: false })
          .limit(1)
          .single()
        );

        if (error) {
          if (error.code === "PGRST116") {
            return null;
          }
          throw error;
        }

        return data?.run_at ? new Date(data.run_at) : null;
      },
      refetchInterval: 60000, // Refetch every minute
    });
  };

  const useTripletexIntegrationsQuery = () => {
    return useQuery({
      queryKey: ["tripletex-integrations", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await withWorkspaceFilter(
          supabase.from("tripletex_integrations")
          .select("*")
          .order("created_at", { ascending: false })
        );

        if (error) throw error;
        return (data || []) as TripletexIntegration[];
      },
    });
  };

  const useActiveTripletexIntegrationsQuery = () => {
    return useQuery({
      queryKey: ["tripletex-integrations-active", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await withWorkspaceFilter(
          supabase.from("tripletex_integrations")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
        );

        if (error) throw error;
        return (data || []) as TripletexIntegration[];
      },
    });
  };

  return {
    useHasActiveTripletexQuery,
    useLastSyncTimeQuery,
    useTripletexIntegrationsQuery,
    useActiveTripletexIntegrationsQuery,
  };
}
